import { ChangeEvent, MouseEvent } from 'react';
import { observable, action, runInAction } from 'mobx';
import { isEmpty, trim } from 'lodash';
import CryptoJS from 'crypto-js';
// @ts-ignore
import bytes from 'utf8-bytes';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import URI from 'urijs';
import posthog from 'posthog-js';

import Service from '@service/login';
import moUser, { getBaseInfo, resetUser } from './user';
import { toggleLoading } from '@store/index';
import {
  createOAuthSessionAuthorizer,
  isOAuthAuthorizationNavigation,
  parseOAuthAuthorizationRequest,
} from '@lib/oauth-authorization';
import { elapsedMs, logLoginTiming, measureLoginRequest, now } from '@tool/login-timing';


const moLogin = observable({
  loginModal: false,
  loginType: 'phone', // account, phone
  rememberPwd: true, // 记住密码
  rememberPhone: true, // 记住手机号
  account: '', // 帐号
  password: '', // 密码
  phone: '', // 手机号
  captcha: '', // 验证码
  accountEmpty: false,
  passwordEmpty: false,
  phoneEmpty: false,
  captchaEmpty: false,
  qrText: '获取验证码',
  qrClass: '',
  phoneErrorText: '手机号码不能为空',
});

// 登录模块初始化
const loginInit = action(() => {
  moLogin.loginType = 'phone';
  moLogin.captcha = '';

  const newURL = new URI(window.location.href);
  const urlEmail = newURL.query(true).email || '';

  if (urlEmail) {
    moLogin.loginType = 'account';
    moLogin.account = urlEmail;
    moLogin.phone = localStorage.getItem('phone') || '';
  } else {
    moLogin.phone = localStorage.getItem('phone') || '';
    moLogin.account = localStorage.getItem('account') || '';
    moLogin.password = localStorage.getItem('password') || '';
  }
});

const toggleLogin = action((value: boolean) => {
  if (value) {
    resetUser();
    moUser.loginHidden = false;
  }
  moLogin.loginModal = value;

});

const changeLoginType = action((value: string) => {
  moLogin.loginType = value;
});

type IKey = 'rememberPwd' | 'rememberPhone';
const toggleValue = action((key: IKey, value: boolean) => {
  moLogin[key] = value;
});

const getOAuthAuthorizationRequest = () => (
  parseOAuthAuthorizationRequest(window.location.search)
);

const authorizeOAuthSession = createOAuthSessionAuthorizer({
  getSearch: () => window.location.search,
  getAccessToken: () => Cookies.get('access_token'),
  clearAccessToken: () => Cookies.remove('access_token'),
  authorize: request => Service.oauthAuthorize(request),
  replaceLocation: callbackUrl => window.location.replace(callbackUrl),
});

// 输入帐号
const changeAccount = action((ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  // console.log('帐号 -- ', ev.target.value);
  moLogin.accountEmpty = false;
  moLogin.account = ev.target.value;
});

// 输入密码
const changePassword = action((ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  // console.log('密码 -- ', ev.target.value);
  moLogin.passwordEmpty = false;
  moLogin.password = ev.target.value;
});

// 输入手机号
const changePhone = action((ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  // console.log('手机号 -- ', ev.target.value);
  if (ev.target.value.length > 11) return;
  moLogin.phoneEmpty = false;
  moLogin.phone = ev.target.value;
});

// 输入验证码
const changeCaptcha = action((ev: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
  // console.log('验证码 -- ', ev.target.value);
  moLogin.captchaEmpty = false;
  moLogin.captcha = ev.target.value;
});

// 倒计时
const countDown = action(async (num: number) => {
  let newNumber: number = num;
  runInAction(() => {
    moLogin.qrText = newNumber.toString();
    moLogin.qrClass = 'disable-text60';

    const timer = setInterval(() => {
      newNumber -= 1;
      runInAction(() => {
        moLogin.qrText = newNumber.toString();
        if (newNumber <= 0) {
          clearInterval(timer);
          moLogin.qrClass = '';
          moLogin.qrText = '获取验证码';
        }
      });
    }, 1000);
  });
});

// 获取验证码
const getCaptcha = action(async (ev: MouseEvent) => {
  ev.stopPropagation();
  if (!trim(moLogin.phone)) {
    moLogin.phoneErrorText = '手机号码不能为空';
    moLogin.phoneEmpty = true;
    return;
  }
  if (trim(moLogin.phone).length !== 11) {
    moLogin.phoneErrorText = '请输入有效的手机号码';
    moLogin.phoneEmpty = true;
    return;
  }
  if (moLogin.qrText !== '获取验证码') return;
  const params = { phone: moLogin.phone };
  const res: any = await measureLoginRequest(
    '发送登录验证码接口',
    () => Service.phoneCaptcha(params),
    response => isEmpty(response) ?
      '空响应' :
      response.code === 0 && response.result?.is_send ? '成功' : '失败',
  );

  if (isEmpty(res)) return;
  if (res.code === 0 && res.result?.is_send) {
    toast.success('验证码发送成功', {
      theme: 'dark',
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
      draggable: false,
      pauseOnFocusLoss: false,
    });
    countDown(60);
  } else {
    toast.error(res.msg, {
      theme: 'dark',
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
      draggable: false,
      pauseOnFocusLoss: false,
    });
  }
});

// 手机号登录
const onPhoneLogin = action(async () => {
  if (!trim(moLogin.phone)) {
    moLogin.phoneErrorText = '手机号码不能为空';
    moLogin.phoneEmpty = true;
    return;
  }
  if (trim(moLogin.phone).length !== 11) {
    moLogin.phoneErrorText = '请输入有效的手机号码';
    moLogin.phoneEmpty = true;
    return;
  }
  if (!trim(moLogin.captcha)) {
    moLogin.captchaEmpty = true;
    return;
  }

  const flowStartedAt = now();
  toggleLoading(true);
  if (moLogin.rememberPhone) localStorage.setItem('phone', moLogin.phone);
  else localStorage.setItem('phone', '');

  const params = {
    phone: trim(moLogin.phone),
    captcha: trim(moLogin.captcha),
  };
  const res: any = await measureLoginRequest(
    '手机验证码登录接口',
    () => Service.phoneLogin(params),
    response => isEmpty(response) ?
      '空响应' :
      response.code === 0 ? '成功' : '失败',
  );

  toggleLoading(false);
  if (isEmpty(res)) {
    logLoginTiming('手机验证码登录页面流程', elapsedMs(flowStartedAt, now()), '未跳转');
    return;
  }
  if (res.code !== 0) {
    toast.error(res.msg, {
      theme: 'dark',
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
      draggable: false,
      pauseOnFocusLoss: false,
    });
    logLoginTiming('手机验证码登录页面流程', elapsedMs(flowStartedAt, now()), '未跳转');
    return;
  }
  // access_token, redirect
  Cookies.set('access_token', res.access_token);
  // console.log('access_token -- ', res.access_token);
  // console.log('redirect -- ', res.redirect);
  // getBaseInfo(() => { window.location.href = '/'; });

  if (getOAuthAuthorizationRequest()) {
    const outcome = await authorizeOAuthSession();
    toggleLoading(false);
    if (outcome !== 'redirecting') {
      toast.error('登录成功，但单点登录授权失败，请稍后重试', {
        theme: 'dark',
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
        draggable: false,
        pauseOnFocusLoss: false,
      });
    }
    return;
  }

  const token = res?.access_token || '';
  const newURL = new URI(window.location.href);
  const urlBack = newURL.query(true).url_name || '';

  logLoginTiming('手机验证码登录页面流程', elapsedMs(flowStartedAt, now()), '已开始跳转');
  if (window.location.href.includes(res.redirect)) {
    if (urlBack) {
      const backUri = new URI(urlBack);
      if (urlBack.includes('callback')) {
        backUri.addQuery('code', token);
      } else {
        backUri.addQuery('token', token);
      }
      window.location.href = backUri.toString();
    } else window.location.href = `${res.redirect}?token=${token}`;
  } else {
    window.location.href = `${res.redirect}?token=${token}`;
  }
});

// 去登录
const toLogin = action(async (flowStartedAt: number) => {
  const from = sessionStorage.getItem('from') || '';
  // ------ 密码加密 ------
  let plaintext = moLogin.password;
  const length = (16 - bytes(plaintext).length % 16) % 16;
  for (let i = 0; i < length; i += 1) plaintext += ' ';
  const pwdString = CryptoJS.enc.Utf8.parse(plaintext);
  const newPwd = CryptoJS.AES.encrypt(pwdString, CryptoJS.enc.Utf8.parse('/H0w<c|:dYV(.q)b'), {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.ZeroPadding,
    iv: CryptoJS.enc.Utf8.parse('/H0w<c|:dYV(.q)b'),
  }).toString();
  // ------ 密码加密 ------

  const params = {
    from,
    user: trim(moLogin.account),
    password: trim(newPwd),
  };
  const res: any = await measureLoginRequest(
    '账号密码登录接口',
    () => Service.accountLogin(params),
    response => response === null || isEmpty(response) ?
      '空响应' :
      response.code === 0 ? '成功' : '失败',
  );

  if (res === null) {
    logLoginTiming('账号密码登录页面流程', elapsedMs(flowStartedAt, now()), '未跳转');
    toast.error('登录失败：服务器返回空响应', {
      theme: 'dark',
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2000,
      draggable: false,
      pauseOnFocusLoss: false,
    });
    toggleLoading(false);
    return;
  }

  if (isEmpty(res) || res.code !== 0) {
    toggleLoading(false);
    logLoginTiming('账号密码登录页面流程', elapsedMs(flowStartedAt, now()), '未跳转');
    return;
  }
  if (!isOAuthAuthorizationNavigation(window.location.search)) {
    posthog.capture('登录成功人数', { username: moLogin.account });
  }
  // access_token, redirect
  Cookies.set('access_token', res.access_token);
  // console.log('access_token -- ', res.access_token);
  // console.log('redirect -- ', res.redirect);

  if (getOAuthAuthorizationRequest()) {
    const outcome = await authorizeOAuthSession();
    toggleLoading(false);
    if (outcome !== 'redirecting') {
      toast.error('登录成功，但单点登录授权失败，请稍后重试', {
        theme: 'dark',
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
        draggable: false,
        pauseOnFocusLoss: false,
      });
    }
    return;
  }

  const token = res.access_token || '';
  const newURL = new URI(window.location.href);
  const urlBack = newURL.query(true).url_name || localStorage.getItem('urlBack') || '';
  toggleLoading(false);
  if (urlBack) {
    localStorage.setItem('urlBack', urlBack);
  }
  if (urlBack.includes('login')) localStorage.setItem('urlBack', '/');
  const newBack = localStorage.getItem('urlBack') || '';

  logLoginTiming('账号密码登录页面流程', elapsedMs(flowStartedAt, now()), '已开始跳转');
  if (window.location.href.includes(res.redirect)) {
    if (newBack) {
      if (newBack.includes('callback')) {
        const backUri = new URI(newBack);
        backUri.addQuery('code', token);
        window.location.href = backUri.toString();
      } else {
        window.location.href = `${newBack}`;
      }
    } else {
      window.location.href = `${res.redirect}`;
    }
  } else {
    window.location.href = `${res.redirect}?token=${token}`;
  }
  // if (window.location.href.includes(res.redirect)) {
  //   if (urlBack) window.location.href = `${urlBack}?token=${token}`;
  //   else window.location.href = `${res.redirect}?token=${token}`;
  // } else {
  //   window.location.href = `${res.redirect}?token=${token}`;
  // }
  // getBaseInfo(() => { window.location.href = '/'; });
});

// 帐号密码登录
const onAccountLogin = action(async () => {
  if (!trim(moLogin.account)) {
    moLogin.accountEmpty = true;
    return;
  }
  if (!trim(moLogin.password)) {
    moLogin.passwordEmpty = true;
    return;
  }

  const flowStartedAt = now();
  toggleLoading(true);
  localStorage.setItem('account', moLogin.account);
  if (moLogin.rememberPwd) {
    localStorage.setItem('password', moLogin.password);
  } else {
    localStorage.setItem('password', '');
  }

  toLogin(flowStartedAt);
});

export default moLogin;
export {
  toggleLogin, changeLoginType, toggleValue,
  changeAccount, changePassword, changePhone, changeCaptcha,
  getCaptcha, onPhoneLogin, onAccountLogin, loginInit,
  authorizeOAuthSession, getOAuthAuthorizationRequest,
};
